/* ============================================================
   predictive-search.js — instant as-you-type search.
   Drives the desktop header dropdown AND the mobile full-screen
   overlay from Shopify's native /search/suggest.json. Tiny, deferred,
   does nothing until an input is focused/typed. No dependencies.

   Wire-up (markup owns the structure; this file owns behavior):
     <input data-predict aria-controls="PANEL_ID"
            data-predict-chips='[{"label":"Oushak","url":"/search?q=oushak"} …]'>
     <div class="predict-panel" id="PANEL_ID"> … filled by render() … </div>
   ============================================================ */
(function () {
  'use strict';

  var SUGGEST = '/search/suggest.json';
  var LIMIT = 6;
  var FIELDS = 'title,product_type,variants.title,variants.sku,vendor,tag';
  var DEBOUNCE = 150;
  var MIN = 2;

  // Format a money string from suggest.json (major-unit decimal, e.g. "1980.00").
  function money(v) {
    var n = parseFloat(v);
    if (isNaN(n)) return '';
    return '$' + Math.round(n).toLocaleString('en-US');
  }
  // Add a width transform to a Shopify CDN image url.
  function img(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function track(event, params) {
    try { if (typeof window.gtag === 'function') window.gtag('event', event, params || {}); } catch (e) {}
  }
  // Pull a "Weave · Size" descriptor from a rug title like
  // "Hand-Knotted Vintage Persian Heriz Wool Rug - 9'4" × 12'8"".
  function descriptor(title) {
    var t = String(title || '');
    var size = t.match(/(\d[\d'"’”\.]*\s*[×x]\s*\d[\d'"’”\.]*)/);
    return size ? size[1].replace(/\s+/g, ' ') : '';
  }

  // Build the panel HTML for a result set. `chips` = curated empty-state chips.
  function render(data, q, chips) {
    var r = (data && data.resources && data.resources.results) || {};
    var products = r.products || [], collections = r.collections || [], queries = r.queries || [];

    // Empty query → show the curated Explore chips only.
    if (!q) {
      if (!chips || !chips.length) return '';
      var c = chips.map(function (ch) {
        return '<a class="predict-chip" data-predict-item data-kind="chip" href="' + esc(ch.url) + '">' + esc(ch.label) + '</a>';
      }).join('');
      return '<div class="predict-empty"><div class="predict-rail-h">Explore</div><div class="predict-chips">' + c + '</div></div>';
    }

    if (!products.length && !collections.length && !queries.length) {
      return '<div class="predict-none">No matches for &ldquo;' + esc(q) + '&rdquo;. ' +
        '<a data-predict-item data-kind="finder" href="/pages/find-my-rug">Let the Rug Finder do the hunting &rarr;</a></div>';
    }

    // LEFT rail — collections + suggested queries.
    var rail = '';
    if (collections.length) {
      rail += '<div class="predict-rail-h">Collections</div>';
      rail += collections.slice(0, 5).map(function (c) {
        return '<a class="predict-sug" data-predict-item data-kind="collection" href="' + esc(c.url) + '">' + esc(c.title) + '</a>';
      }).join('');
    }
    if (queries.length) {
      rail += '<div class="predict-rail-h">Suggestions</div>';
      rail += queries.slice(0, 5).map(function (qq) {
        var qtext = qq.text || qq.title || '';
        // Scope suggestion clicks to products (not pages/articles), matching the rest of search.
        var qurl = '/search?q=' + encodeURIComponent(qtext) + '&type=product&options%5Bprefix%5D=last';
        return '<a class="predict-sug" data-predict-item data-kind="query" href="' + esc(qurl) + '">' + esc(qtext) + '</a>';
      }).join('');
    }
    if (!rail && chips && chips.length) {
      rail = '<div class="predict-rail-h">Explore</div>' + chips.map(function (ch) {
        return '<a class="predict-sug" data-predict-item data-kind="chip" href="' + esc(ch.url) + '">' + esc(ch.label) + '</a>';
      }).join('');
    }

    // RIGHT — product grid.
    var grid = products.slice(0, LIMIT).map(function (p) {
      var src = p.image || (p.featured_image && p.featured_image.url) || '';
      var d = descriptor(p.title);
      return '<a class="predict-prod" data-predict-item data-kind="product" href="' + esc(p.url) + '">' +
        '<span class="predict-prod-img">' + (src ? '<img src="' + esc(img(src, 200)) + '" alt="" loading="lazy" decoding="async">' : '') + '</span>' +
        '<span class="predict-prod-meta">' +
          '<span class="predict-prod-name">' + esc(p.title) + '</span>' +
          (d ? '<span class="predict-prod-sub">' + esc(d) + '</span>' : '') +
          '<span class="predict-prod-price">' + esc(money(p.price)) + '</span>' +
        '</span></a>';
    }).join('');

    var seeAll = '<a class="predict-all" data-predict-item data-kind="all" href="/search?q=' +
      encodeURIComponent(q) + '&type=product&options%5Bprefix%5D=last">See all results for &ldquo;' + esc(q) + '&rdquo; &rarr;</a>';

    return '<div class="predict-cols">' +
      '<div class="predict-rail">' + rail + '</div>' +
      '<div class="predict-grid">' + (grid || '<div class="predict-none">No pieces matched — try a weave, color, or size.</div>') + '</div>' +
    '</div>' + seeAll;
  }

  function init(input) {
    var panelId = input.getAttribute('aria-controls');
    var panel = panelId && document.getElementById(panelId);
    if (!panel) return;
    var chips = [];
    try { chips = JSON.parse(input.getAttribute('data-predict-chips') || '[]'); } catch (e) {}

    var timer = null, ctrl = null, active = -1, seq = 0;

    function items() { return Array.prototype.slice.call(panel.querySelectorAll('[data-predict-item]')); }
    function setOpen(open) {
      panel.classList.toggle('open', open);
      input.setAttribute('aria-expanded', String(open));
      if (!open) { active = -1; }
    }
    function paint(html) {
      panel.innerHTML = html;
      active = -1;
      input.removeAttribute('aria-activedescendant');
      setOpen(html !== '');
      // Give each option an id so aria-activedescendant can point to it (a11y),
      // then wire GA4 on any result click.
      items().forEach(function (el, i) {
        el.id = el.id || (panel.id + '-opt-' + i);
        el.addEventListener('click', function () {
          var kind = el.getAttribute('data-kind');
          if (kind === 'product') track('search_result_click', { search_term: input.value, item: el.querySelector('.predict-prod-name') ? el.querySelector('.predict-prod-name').textContent : '' });
          else track('search_suggest_click', { search_term: input.value, kind: kind });
        });
      });
    }
    function highlight() {
      var list = items();
      list.forEach(function (el, i) { el.classList.toggle('is-active', i === active); });
      if (active > -1 && list[active]) {
        input.setAttribute('aria-activedescendant', list[active].id || '');
        list[active].scrollIntoView({ block: 'nearest' });
      }
    }
    function go() {
      var q = input.value.trim();
      if (q.length < MIN) { paint(render(null, '', chips)); return; }
      if (ctrl) ctrl.abort();
      ctrl = ('AbortController' in window) ? new AbortController() : null;
      // Monotonic token so the latest request always wins, even when
      // AbortController is unavailable (legacy engines) and can't cancel.
      var token = ++seq;
      var url = SUGGEST + '?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection,query&resources[limit]=' + LIMIT +
        '&resources[options][unavailable_products]=last&resources[options][fields]=' + encodeURIComponent(FIELDS);
      fetch(url, { signal: ctrl ? ctrl.signal : undefined, headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (data) { if (token === seq) paint(render(data, q, chips)); })
        .catch(function (e) { if (token === seq && e && e.name !== 'AbortError') paint(''); });
    }

    input.addEventListener('input', function () { clearTimeout(timer); timer = setTimeout(go, DEBOUNCE); });
    input.addEventListener('focus', function () { if (input.value.trim().length < MIN) paint(render(null, '', chips)); else setOpen(true); });
    input.addEventListener('keydown', function (e) {
      var list = items();
      if (e.key === 'ArrowDown') { e.preventDefault(); active = Math.min(active + 1, list.length - 1); highlight(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); active = Math.max(active - 1, -1); highlight(); }
      else if (e.key === 'Enter') { if (active > -1 && list[active]) { e.preventDefault(); list[active].click(); } }
      else if (e.key === 'Escape') { setOpen(false); }
    });
    // Click-out closes (desktop dropdown only; overlay manages its own open state).
    document.addEventListener('click', function (e) {
      if (!panel.classList.contains('open')) return;
      if (e.target === input || panel.contains(e.target) || input.contains(e.target)) return;
      setOpen(false);
    });
  }

  function boot() {
    var inputs = document.querySelectorAll('input[data-predict]');
    Array.prototype.forEach.call(inputs, init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Expose render for tests/manual use.
  window.ArteverkPredict = { render: render };
})();
